// routes/admin/PushOverviewForUser.tsx
import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { ENV } from "../../env";

type PushEntry = {
  id: string;
  token: string;
  platform: string | null;
  device_info: string | null;
  updated_at: string | null;
};

export default function PushOverviewForUser({ userId }: { userId: string }) {
  const [entries, setEntries] = useState<PushEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTokens = async () => {
    const { data, error } = await supabase
      .from("user_push_tokens")
      .select("id, token, platform, device_info, updated_at")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("❌ Fehler beim Laden der Push-Tokens:", error);
    } else {
      setEntries(data);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadTokens();
  }, [userId]);

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("user_push_tokens")
      .delete()
      .eq("id", id);
    if (error) {
      console.error("❌ Fehler beim Löschen:", error);
    } else {
      setEntries((prev) => prev.filter((e) => e.id !== id));
    }
  };

  const handleTestPush = async () => {
    if (entries.length === 0) {
      alert("❌ Kein Push-Token vorhanden");
      return;
    }

    const session = await supabase.auth.getSession();
    const accessToken = session.data.session?.access_token;
    if (!accessToken) {
      alert("❌ Nicht eingeloggt");
      return;
    }

    const res = await fetch(`${ENV.FUNCTION_ENDPOINT}/dispatch-push`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        record: { user_id: userId, todo_id: "test-todo-id" },
      }),
    });

    if (!res.ok) {
      const error = await res.text();
      alert(`❌ Fehler: ${res.status} – ${error}`);
      return;
    }

    alert("✅ Test-Dispatch an Supabase versendet");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-100">
          📬 Push-Token für diesen Nutzer
        </h2>
        <button
          onClick={handleTestPush}
          className="text-sm px-3 py-1 bg-sky-500 text-white rounded hover:bg-sky-600 transition-colors"
        >
          🚀 Test-Push senden
        </button>
      </div>

      {loading ? (
        <p className="text-slate-400">Lade Einträge …</p>
      ) : entries.length === 0 ? (
        <p className="text-slate-500">Keine Push-Tokens vorhanden.</p>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="border border-[#2a3347] rounded-xl p-4 bg-[#1e2639] flex justify-between items-center"
            >
              <div>
                <p className="text-sm text-slate-300">
                  <strong>Plattform:</strong> {entry.platform}
                </p>
                <p className="text-sm text-slate-300">
                  <strong>Gerät:</strong> {entry.device_info}
                </p>
                <p className="text-xs text-slate-500">
                  Letzte Aktualisierung:{" "}
                  {new Date(entry.updated_at ?? "").toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => handleDelete(entry.id)}
                className="text-sm text-red-400 hover:underline"
              >
                🗑️ Löschen
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
